import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { API_ENDPOINTS } from '../../utils/GlobalAPI';
import Pagination from './Pagination';
import AdminPageHeader from './AdminPageHeader';
import { FaHandshake, FaInstagram, FaExternalLinkAlt, FaSearch, FaFilter } from 'react-icons/fa';

const AdminHireMe = () => {
  const [hireRequests, setHireRequests] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [filter, setFilter] = useState('');
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [hireRes, infRes] = await Promise.all([
          fetch(API_ENDPOINTS.HIRE_REQUESTS),
          fetch(API_ENDPOINTS.INFLUENCER)
        ]);

        const hireData = await hireRes.json();
        const infData = await infRes.json();

        setHireRequests(hireData);
        setInfluencers(infData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleFilter = (value) => {
    setFilter(value);
    const filteredData = hireRequests.filter(
      (r) =>
        r.name.toLowerCase().includes(value.toLowerCase()) ||
        r.email.toLowerCase().includes(value.toLowerCase())
    );
    setFiltered(filteredData);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const getInfluencerDetails = (influencerData) => {
    // If influencerData is already populated (object), return it
    if (influencerData && typeof influencerData === 'object' && influencerData._id) {
      return influencerData;
    }
    // If it's just an ID string, find it in the influencers array
    if (typeof influencerData === 'string') {
      return influencers.find((inf) => inf._id === influencerData);
    }
    return null;
  };

  const dataToShow = filter ? filtered : hireRequests;
  
  // Pagination logic
  const totalPages = Math.ceil(dataToShow.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = dataToShow.slice(startIndex, endIndex);

  const handlePageChange = (page, newItemsPerPage = itemsPerPage) => {
    setCurrentPage(page);
    if (newItemsPerPage !== itemsPerPage) {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1);
    }
  };

  return (
    <div className="bg-slate-50/50 min-h-screen">
      <AdminPageHeader
        title="Collab Request Logs"
        subtitle="Monitor and process brand collaboration requests for network creators"
        icon={<FaHandshake className="w-6 h-6" />}
      />

      <div className="pt-32 p-6 max-w-7xl mx-auto space-y-6">
        {/* Notice */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-amber-50 border border-amber-200/50 rounded-2xl p-4 flex gap-3 shadow-sm"
        >
          <div className="text-amber-500 mt-0.5">
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-0.5">Legacy Data Notice</h4>
            <p className="text-xs text-amber-700 font-medium">Some legacy hire requests may show "No Creator Linked" because they were created prior to the relational ID system. Newer requests properly display and reference creator links.</p>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
          className="grid grid-cols-2 md:grid-cols-5 gap-4"
        >
          {[
            { label: 'Total Requests', val: hireRequests.length, color: 'text-slate-800', bg: 'bg-white border-slate-100' },
            { label: 'Linked Roster', val: hireRequests.filter(req => req.influencerId).length, color: 'text-emerald-600', bg: 'bg-white border-emerald-100' },
            { label: 'Unlinked Log', val: hireRequests.filter(req => !req.influencerId).length, color: 'text-amber-600', bg: 'bg-white border-amber-100' },
            { label: 'Network Roster', val: influencers.length, color: 'text-blue-600', bg: 'bg-white border-blue-100' },
            { label: 'Premium Collabs', val: hireRequests.filter(req => req.isPremiumInfluencer).length, color: 'text-purple-600', bg: 'bg-white border-purple-100' }
          ].map((stat, i) => (
            <div key={i} className={`border rounded-2xl p-5 shadow-sm hover:shadow transition-shadow ${stat.bg}`}>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{stat.label}</div>
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.val}</div>
            </div>
          ))}
        </motion.div>

        {/* Search Control Bar */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex items-center justify-between"
        >
          <div className="relative w-full md:max-w-md">
            <FaSearch className="absolute left-3 top-3.5 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by client name or email address..."
              value={filter}
              onChange={(e) => handleFilter(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-slate-800 text-sm focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition placeholder:text-slate-400"
            />
          </div>
          <div className="hidden md:flex items-center text-xs font-semibold text-slate-400 gap-1">
            <FaFilter className="w-3.5 h-3.5" />
            <span>Real-time Filtering</span>
          </div>
        </motion.div>

        {/* Table representation */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white border border-slate-100 rounded-2xl shadow-sm">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mb-4"></div>
            <p className="text-sm text-slate-500">Retrieving request log...</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="space-y-4"
          >
            <div className="overflow-x-auto bg-white rounded-2xl border border-slate-100 shadow-sm">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Client (Requester)</th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Target Creator</th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Collab Tier & Details</th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Submitted Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-16 text-slate-500">
                        <div className="flex flex-col items-center">
                          <div className="w-14 h-14 bg-slate-50 rounded-full flex items-center justify-center mb-3 border border-slate-100">
                            <FaHandshake className="w-6 h-6 text-slate-400" />
                          </div>
                          <p className="text-base font-semibold text-slate-700">No hire logs found</p>
                          <p className="text-xs text-slate-400 mt-1">Try adjusting your search criteria</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((req, index) => {
                      const inf = getInfluencerDetails(req.influencerId);
                      const hasInfluencerData = inf && inf._id;
                      const isPremium = req.isPremiumInfluencer || false;
                      const influencerName = isPremium 
                        ? (req.premiumInfluencerName || 'Premium Influencer')
                        : (hasInfluencerData ? inf.name : 'No Creator Linked');

                      return (
                        <tr key={index} className="hover:bg-slate-50/40 transition-colors">
                          {/* Client column */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="font-semibold text-slate-800 text-sm">{req.name}</div>
                              <div className="text-[11px] text-slate-400 mt-0.5">{req.email}</div>
                              {req.phone && (
                                <div className="text-[10px] text-slate-400 font-mono mt-0.5">{req.phone}</div>
                              )}
                            </div>
                          </td>

                          {/* Target Creator column */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="font-semibold text-slate-800 text-sm flex items-center gap-1.5">
                                {influencerName}
                                {isPremium && <span className="text-[10px] bg-amber-100 border border-amber-200 text-amber-800 px-1 rounded font-bold">VIP</span>}
                              </div>
                              {hasInfluencerData ? (
                                <>
                                  <div className="text-[11px] text-slate-400 mt-0.5">{inf.email}</div>
                                  <div className="text-[10px] text-slate-400 font-mono mt-0.5">{inf.phone}</div>
                                  {inf.instagram && (
                                    <div className="mt-1">
                                      <a
                                        href={inf.instagram}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-purple-50 to-pink-50 border border-pink-200/40 text-pink-700 rounded text-[10px] font-semibold"
                                      >
                                        <FaInstagram className="w-2.5 h-2.5 text-pink-500" />
                                        <span>Instagram</span>
                                        <FaExternalLinkAlt className="w-2 h-2 opacity-65" />
                                      </a>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="text-[11px] text-slate-400 italic mt-0.5">Relational logs unavailable</div>
                              )}
                            </div>
                          </td>

                          {/* Collab Tier & Details */}
                          <td className="px-6 py-4 whitespace-nowrap space-y-1">
                            <div>
                              {isPremium ? (
                                <span className="inline-block px-2.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200/50">
                                  ⭐ PREMIUM
                                </span>
                              ) : (
                                <span className="inline-block px-2.5 py-0.5 rounded text-[10px] font-bold bg-slate-50 text-slate-600 border border-slate-200/50">
                                  STANDARD
                                </span>
                              )}
                            </div>
                            {hasInfluencerData && inf.influencerId && (
                              <div className="text-[10px] font-mono text-slate-600 bg-slate-50 border border-slate-100 rounded px-1.5 py-0.5 inline-block">
                                UID: {inf.influencerId}
                              </div>
                            )}
                          </td>

                          {/* Submitted date */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 font-medium">
                            {new Date(req.createdAt || req.date || Date.now()).toLocaleString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              totalItems={dataToShow.length}
              showItemsPerPage={true}
              itemsPerPageOptions={[5, 10, 20, 50]}
            />
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminHireMe;
